import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'
import { RefinementCtx, z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import {
  CustomizationField,
  isMandatory,
  subcategoriesForCategory,
} from 'modules/expenses/hooks/useExpensesCustomization'

export interface SuperRefineContext {
  data: {
    category?: string | null
    [key: string]: unknown
  }
  ctx: RefinementCtx
  customizationFields: CustomizationField[]
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
}

// FormContext contains the auxiliary data that is needed for validation, in this case is the customizationFields
export interface FormContext {
  customizationFields: CustomizationField[]
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
}

export const CategorySchema = () => {
  return z.object({
    category: z.string().nullish(),
    subcategory: z.string().nullish(),
  })
}

export const superRefineCategorySchema = ({
  data,
  ctx,
  customizationFields,
  resourceType,
}: SuperRefineContext) => {
  // Category only exists on the customization fields for expenses, so unless we check for resource type, we won't ever validate subcategories on mileages or per diems
  if (shouldShowCategoryBlock(resourceType)) {
    if (!isMandatory('category', resourceType, customizationFields)) {
      return
    }
  }

  const selectedCategory = data.category
  if (!selectedCategory) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18n.t('expenses_v2.forms.errors.required'),
      path: ['category'],
    })
    return
  }

  if (
    !isMandatory('subcategory', resourceType, customizationFields) ||
    !hasSubcategories(selectedCategory, customizationFields, resourceType)
  ) {
    return
  }

  const selectedSubcategory = data.subcategory
  if (!selectedSubcategory) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18n.t('expenses_v2.forms.errors.required'),
      path: ['subcategory'],
    })
    return
  }
}

const hasSubcategories = (
  category: string,
  customizationFields: CustomizationField[],
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
) => {
  return subcategoriesForCategory(customizationFields, resourceType, category)?.length > 0
}

export const shouldShowCategoryBlock = (
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
): boolean => {
  const isExpense = resourceType === 'expense'

  return isExpense ? true : false
}
export type CategorySchema = z.infer<ReturnType<typeof CategorySchema>>
